import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Drives the Marketing screen.
//
// Fetches all promotions for the user's restaurant (RLS auto-scopes)
// and groups them by status: live (currently running), planned
// (future-dated or planned status), past (completed or end-date in
// the past). The grouping is computed in JS so a promo whose
// ends_on has passed automatically rolls from "live" to "past"
// without needing a cron job to flip status.
//
// Mutators:
//   createPromotion(payload)   — insert
//   updatePromotion(id, patch) — update one
//   deletePromotion(id)        — hard delete
//   setStatus(id, status)      — one-tap status change for the row UI
export function usePromotionsData() {
  const [promotions, setPromotions] = useState([])
  const [restaurant, setRestaurant] = useState(null)  // for AI generator brand context
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [promosRes, restaurantRes] = await Promise.all([
        supabase
          .from('promotions')
          .select(`
            id, name, description, starts_on, ends_on, channels,
            cost_estimate, status, notes, style_notes,
            created_at, updated_at
          `)
          .order('starts_on', { ascending: false }),
        supabase
          .from('restaurants')
          .select('id, name, timezone')
          .eq('active', true)
          .order('created_at')
          .limit(1)
          .maybeSingle(),
      ])
      if (promosRes.error) throw promosRes.error
      setPromotions(promosRes.data ?? [])
      setRestaurant(restaurantRes.data ?? null)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[usePromotionsData]', e)
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Grouping — derived live so editing dates re-buckets correctly.
  // "live" = status is 'planned' or 'live' AND today is between
  //          starts_on and ends_on (inclusive). Treat missing
  //          ends_on as open-ended (live until manager closes it).
  // "planned" = status is 'planned' AND starts_on > today.
  // "past"  = status is 'completed' or 'cancelled' OR ends_on < today.
  const grouped = useMemo(() => {
    const today = new Date().toLocaleDateString('en-CA') // YYYY-MM-DD
    const live = [], planned = [], past = []
    for (const p of promotions) {
      const start = p.starts_on
      const end = p.ends_on
      if (p.status === 'cancelled' || p.status === 'completed') {
        past.push(p)
        continue
      }
      if (end && end < today) {
        past.push(p)
        continue
      }
      if (start <= today) {
        live.push(p)
      } else {
        planned.push(p)
      }
    }
    // Live: most recent start first; Planned: nearest start first;
    // Past: most recent end first.
    live.sort((a, b) => (a.starts_on < b.starts_on ? 1 : -1))
    planned.sort((a, b) => (a.starts_on > b.starts_on ? 1 : -1))
    past.sort((a, b) => {
      const ae = a.ends_on || a.starts_on
      const be = b.ends_on || b.starts_on
      return ae < be ? 1 : -1
    })
    return { live, planned, past }
  }, [promotions])

  // ---------- Create ----------------------------------------
  const createPromotion = useCallback(async (payload) => {
    const insert = {
      name:          payload.name?.trim(),
      description:   payload.description?.trim() || null,
      starts_on:     payload.starts_on,
      ends_on:       payload.ends_on || null,
      channels:      Array.isArray(payload.channels) ? payload.channels : [],
      cost_estimate: payload.cost_estimate != null && payload.cost_estimate !== ''
                       ? Number(payload.cost_estimate) : null,
      status:        payload.status || 'planned',
      notes:         payload.notes?.trim() || null,
      style_notes:   payload.style_notes?.trim() || null,
    }
    if (!insert.name) throw new Error('Promotion name is required')
    if (!insert.starts_on) throw new Error('Start date is required')
    const { data, error: insErr } = await supabase
      .from('promotions')
      .insert(insert)
      .select()
      .single()
    if (insErr) throw insErr
    await fetchAll()
    return data
  }, [fetchAll])

  // ---------- Update ----------------------------------------
  const updatePromotion = useCallback(async (id, patch) => {
    const updates = { ...patch, updated_at: new Date().toISOString() }
    const { error: updErr } = await supabase
      .from('promotions')
      .update(updates)
      .eq('id', id)
    if (updErr) throw updErr
    await fetchAll()
  }, [fetchAll])

  // ---------- Status flip ----------------------------------
  // One-tap convenience for the row UI ("Mark complete", "Cancel").
  const setStatus = useCallback(async (id, status) => {
    return updatePromotion(id, { status })
  }, [updatePromotion])

  // ---------- Delete ---------------------------------------
  const deletePromotion = useCallback(async (id) => {
    const { error: delErr } = await supabase
      .from('promotions')
      .delete()
      .eq('id', id)
    if (delErr) throw delErr
    await fetchAll()
  }, [fetchAll])

  return {
    promotions,
    grouped,
    restaurant,
    loading,
    error,
    refresh: fetchAll,
    createPromotion,
    updatePromotion,
    setStatus,
    deletePromotion,
  }
}
